import { Stack } from "@mui/material";
import { Badge, Button, Modal, Text } from "@shopify/polaris";
import React, { useCallback, useState } from "react";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";

import { IntegrationBrands, IntegrationLogo } from "./integrationLogos";
import { AccountStatus, CardInner, ManualIntegrationCard, ModalWrapper } from "./styles";
import { IntegrationSectionProps } from "./types";
import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";

export function Klaviyo({ onDelete, loading, savedValue }: IntegrationSectionProps): JSX.Element {
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const modalChange = useCallback(() => setActive(!active), [active]);
  const { addToast } = useToast();

  const connectedAndSaved = savedValue?.apiKey === "CONNECTED";
  const onConnect = useCallback(async () => {
    setBusy(true);
    try {
      const res = await typedFrontendVendorApi.getReq("/integrations/klaviyo/connect");
      if (res.type === "error") {
        addToast("Error connecting to Klaviyo.");
        return;
      }
      const { authUrl } = res.body;
      window.open(authUrl, "_self");
    } finally {
      setBusy(false);
    }
  }, []);
  const title = connectedAndSaved ? "Disconnect Klaviyo" : "Connect Klaviyo";

  return (
    <ManualIntegrationCard data-testid="klaviyo-component">
      <ModalWrapper>
        <Modal
          open={!active}
          onClose={modalChange}
          title={title}
          primaryAction={{
            content: "Confirm",
            loading: loading || busy,
            disabled: false,
            onAction() {
              if (connectedAndSaved) {
                onDelete();
                modalChange();
              } else {
                onConnect();
              }
            },
          }}
          secondaryActions={[
            {
              content: "Cancel",
              loading,
              onAction: modalChange,
            },
          ]}
        >
          <Modal.Section>
            {connectedAndSaved ? (
              <p data-testid="disconnect-description">
                Disconnecting your Klaviyo account will stop the flow of subscription event data from Smartrr to
                Klaviyo. This means that any email or SMS flows in Klaviyo that rely on this data will no longer
                be triggered by Smartrr events. You can reconnect your account at any time. Are you sure you want
                to disconnect?
              </p>
            ) : (
              <React.Fragment>
                <p data-testid="connect-description">
                  Connecting your Klaviyo account will allow Smartrr to seamlessly sync subscription event data,
                  enabling you to create powerful email and SMS marketing flows. This integration helps you
                  automate customer communication, personalize messaging, and ultimately drive increased
                  engagement and revenue. To complete the connection, you&apos;ll be redirected to Klaviyo to
                  authorize Smartrr&apos;s access. After authorization, you&apos;ll be redirected back to Smartrr.
                </p>
              </React.Fragment>
            )}
          </Modal.Section>
        </Modal>
      </ModalWrapper>
      <CardInner>
        <CardInner>
          <Stack direction="column" spacing="10px">
            <Stack direction="row" justifyContent="space-between" alignItems="self-start">
              <Stack direction="row" spacing="10px">
                <div className="integration-logo">
                  <IntegrationLogo brand={IntegrationBrands.Klaviyo} />
                </div>
                <Stack direction="column" spacing="3px">
                  <Text variant="headingMd" as="h2">
                    Klaviyo
                  </Text>
                  {connectedAndSaved ? (
                    <AccountStatus>Account connected</AccountStatus>
                  ) : (
                    <AccountStatus>No account connected</AccountStatus>
                  )}
                </Stack>
              </Stack>
              {connectedAndSaved ? (
                <Button onClick={modalChange}>Disconnect</Button>
              ) : (
                <Button primary onClick={modalChange}>
                  Connect
                </Button>
              )}
            </Stack>
            <div className="integration-description">
              Connect to sync subscription event data to Klaviyo to set up email and SMS flows and notifications.
            </div>
            <div className="integration-attributes">
              <Badge>Emails</Badge>
              <Badge>SMS</Badge>
            </div>
            <div className="integration-more-info">
              <a
                target="_blank"
                href="https://help.smartrr.com/docs/support/integrations/klaviyo-x-smartrr-integration"
                rel="noreferrer"
              >
                More Info
              </a>
            </div>
          </Stack>
        </CardInner>
      </CardInner>
    </ManualIntegrationCard>
  );
}
